import { useEffect, useRef, useState } from 'react';
import { DownArrowIcon } from '../../../../assets/icons';
import styles from './TablePaginationV2.module.scss';

interface TablePaginationV2Props {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  className?: string;
  isLoading?: boolean;
}

export function TablePaginationV2({
  totalItems,
  itemsPerPage,
  currentPage,
  pageSizeOptions = [10, 20, 50],
  onPageChange,
  onPageSizeChange,
  className,
  isLoading,
}: Readonly<TablePaginationV2Props>) {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isLoading || totalItems === 0) return null;

  const getPageWindow = (): number[] => {
    const windowSize = 4;
    let start = Math.max(1, currentPage - 1);
    let end = start + windowSize - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - windowSize + 1);
    }

    const pages: number[] = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const pageWindow = getPageWindow();
  const startItem = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className={`${styles.container} ${className ?? ''}`}>
      <div className={styles.leftSection}>
        <div className={styles.pageSizeSelector} ref={dropdownRef}>
        <button
          type="button"
          className={styles.dropdownTrigger}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          {itemsPerPage} per page
          <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}><DownArrowIcon /></span>
        </button>

        {isOpen && (
          <div className={styles.dropdownMenu}>
            {pageSizeOptions.map((size) => (
              <button
                key={size}
                type="button"
                className={`${styles.dropdownItem} ${size === itemsPerPage ? styles.dropdownItemActive : ''}`}
                onClick={() => { onPageSizeChange(size); setIsOpen(false); }}
              >
                {size} per page
              </button>
            ))}
          </div>
        )}
        </div>
        <span className={styles.entriesText}>
          Showing {startItem} to {endItem} of {totalItems} entries
        </span>
      </div>

      <div className={styles.pageControls}>
        <button
          className={styles.navButton}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <DownArrowIcon className={styles.iconLeft} />
          Previous
        </button>

        {pageWindow.map((page) => (
          <button
            key={page}
            className={`${styles.pageButton} ${currentPage === page ? styles.active : ''}`}
            onClick={() => onPageChange(page)}
          >
            {page}
          </button>
        ))}

        <button
          className={styles.navButton}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
          <DownArrowIcon className={styles.iconRight} />
        </button>
      </div>
    </div>
  );
}
